/* AE Products Slider — carousel mechanics + iOS-safe More Info modal.
   One IIFE per page. Live-reads slide width + gap on every tick so
   responsive resize works.

   The modal follows the WebKit-bug-172206-safe pattern:
     * No CSS transforms anywhere on the modal/image
     * Show/hide via display: none ↔ flex (.is-open)
     * Slide switching via display: none ↔ block (.is-active)
     * Backdrop is a sibling div with proper pointer-events chain
     * Body scroll lock via overflow: hidden on <html>
     * rAF + { preventScroll: true } on focus moves */
(function () {
  'use strict';

  /* ===== Carousel — native scroll + scroll-snap.
     Touch: user swipes the viewport, browser handles the scroll.
     Arrows: scrollBy() one slide width left/right with smooth behavior.
     Progress bar: reflects current scrollLeft / max scrollable distance. */
  function initSlider(root) {
    if (!root || root.dataset.aeProductsInit === '1') return;
    root.dataset.aeProductsInit = '1';

    var viewport = root.querySelector('[data-ae-products-viewport]');
    var track = root.querySelector('[data-ae-products-track]');
    var prev = root.querySelector('[data-ae-products-prev]');
    var next = root.querySelector('[data-ae-products-next]');
    var progress = root.querySelector('[data-ae-products-progress]');
    if (!viewport || !track) return;

    var slides = track.querySelectorAll('.ae-products-slider__slide');
    if (!slides.length) return;

    function getSlideStep() {
      // Distance to advance per arrow click — one slide width
      var rect = slides[0].getBoundingClientRect();
      return Math.max(1, rect.width);
    }

    function maxScroll() {
      return Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    }

    function update() {
      var sl = viewport.scrollLeft;
      var max = maxScroll();

      if (prev) prev.disabled = sl <= 1;
      if (next) next.disabled = sl >= max - 1;

      if (progress) {
        // Ratio = how much of the scrollable distance you've covered,
        // padded by the visible portion so it never sits at 0.
        var ratio;
        if (max <= 0) {
          ratio = 1; // everything visible, fill the bar
        } else {
          ratio = (sl + viewport.clientWidth) / viewport.scrollWidth;
        }
        var pct = Math.min(100, Math.max(12, ratio * 100));
        progress.style.transform = 'scaleX(' + (pct / 100) + ')';
      }
    }

    function scrollByOne(direction) {
      var step = getSlideStep();
      var target = viewport.scrollLeft + (direction * step);
      // Clamp so we don't overshoot
      target = Math.max(0, Math.min(maxScroll(), target));
      if (typeof viewport.scrollTo === 'function') {
        viewport.scrollTo({ left: target, behavior: 'smooth' });
      } else {
        viewport.scrollLeft = target;
      }
    }

    if (prev) prev.addEventListener('click', function () { scrollByOne(-1); });
    if (next) next.addEventListener('click', function () { scrollByOne(1); });

    var scrollTimer;
    viewport.addEventListener('scroll', function () {
      if (scrollTimer) cancelAnimationFrame(scrollTimer);
      scrollTimer = requestAnimationFrame(update);
    }, { passive: true });

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(update, 120);
    });

    update();
  }

  /* ===== iOS-safe More Info modal =====
     One initialiser per section. Each section owns its own modal +
     slides. We bind close handlers + ESC to the section's modal once.
     Trigger clicks are handled via document-level delegation so
     dynamically-added cards just work. */
  var modalSections = []; // array of { section, modal } for ESC handling

  function initModal(section) {
    if (!section || section.dataset.aeProductsModalInit === '1') return;
    section.dataset.aeProductsModalInit = '1';

    var modal = section.querySelector('[data-ae-products-modal]');
    if (!modal) return;

    var closeEls = modal.querySelectorAll('[data-ae-products-modal-close]');
    closeEls.forEach(function (el) {
      el.addEventListener('click', function (e) {
        e.preventDefault();
        closeModal(modal);
      });
    });

    modalSections.push({ section: section, modal: modal });
  }

  function openModal(modal, btn) {
    if (!modal || !btn) return;
    var id = btn.getAttribute('data-info-id');
    if (!id) return;

    // Mark the correct slide active (display: none -> block)
    var slides = modal.querySelectorAll('[data-info-id]');
    var found = false;
    slides.forEach(function (s) {
      var active = s.getAttribute('data-info-id') === id;
      s.classList.toggle('is-active', active);
      if (active) found = true;
    });
    if (!found) return; // no matching slide — bail without opening

    modal._lastFocused = document.activeElement;
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('ae-products-modal-open');

    // rAF so the modal is rendered before we move focus, and preventScroll
    // so iOS doesn't jump the viewport when focusing the close button.
    requestAnimationFrame(function () {
      var closeBtn = modal.querySelector('.ae-products-slider__modal-close');
      if (closeBtn && typeof closeBtn.focus === 'function') {
        try { closeBtn.focus({ preventScroll: true }); }
        catch (_e) { closeBtn.focus(); }
      }
    });
  }

  function closeModal(modal) {
    if (!modal || !modal.classList.contains('is-open')) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('ae-products-modal-open');

    // Clear active slide so we don't have a stale display: block
    var actives = modal.querySelectorAll('[data-info-id].is-active');
    actives.forEach(function (s) { s.classList.remove('is-active'); });

    var last = modal._lastFocused;
    if (last && typeof last.focus === 'function') {
      try { last.focus({ preventScroll: true }); }
      catch (_e) { last.focus(); }
    }
    modal._lastFocused = null;
  }

  /* Document-level click delegation for More Info triggers. Means new
     cards (e.g. after a section reload in the editor) just work. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-ae-info-open]');
    if (!btn) return;
    e.preventDefault();
    var section = btn.closest('.ae-products-slider');
    if (!section) return;
    var modal = section.querySelector('[data-ae-products-modal]');
    if (!modal) return;
    openModal(modal, btn);
  });

  /* ESC closes any currently-open modal */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' && e.keyCode !== 27) return;
    for (var i = 0; i < modalSections.length; i++) {
      var m = modalSections[i].modal;
      if (m.classList.contains('is-open')) {
        e.preventDefault();
        closeModal(m);
      }
    }
  });

  /* ===== Bootstrap ===== */
  function bootstrap(root) {
    var scope = root || document;
    var sliders = scope.querySelectorAll('[data-ae-products-slider]');
    sliders.forEach(initSlider);
    var sections = scope.querySelectorAll('.ae-products-slider');
    sections.forEach(initModal);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { bootstrap(document); });
  } else {
    bootstrap(document);
  }

  /* Re-init on theme-editor section reload */
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.classList && e.target.classList.contains('section-ae-products-slider')) {
      e.target.querySelectorAll('[data-ae-products-slider]').forEach(function (el) {
        el.dataset.aeProductsInit = '';
        initSlider(el);
      });
      e.target.dataset.aeProductsModalInit = '';
      initModal(e.target);
    }
  });
})();
